import {
  Component, ElementRef, ViewChild, AfterViewInit, OnInit, OnDestroy,
  ChangeDetectionStrategy, ChangeDetectorRef,
} from '@angular/core';
import { BaseListboxEditor } from './base-editor';
import { CELL_EDITOR_STYLES } from './cell-editor.styles';
import { ChildTableA11yService } from '../../services/child-table-a11y.service';
import { ChildTablePositioningService } from '../../services/child-table-positioning.service';

@Component({
  selector: 'ct-select-editor',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [CELL_EDITOR_STYLES],
  template: `
    <div class="cell-listbox-wrapper">
      <button #trigger class="cell-listbox-trigger" type="button"
        [attr.data-value]="editingValue"
        role="combobox"
        [attr.aria-expanded]="true"
        aria-haspopup="listbox"
        [attr.aria-controls]="listboxId"
        [attr.aria-activedescendant]="activeDescendantId"
        [attr.aria-label]="column.label + ': ' + (getOptionLabel(editingValue) || 'none selected')"
        [attr.aria-describedby]="ariaDescribedBy"
        (keydown)="onListboxKeydown($event)">
        {{ getOptionLabel(editingValue) || 'Select...' }}
        <lucide-angular class="select-chevron" name="chevron-down" [size]="12" aria-hidden="true"></lucide-angular>
      </button>
      <div class="cell-listbox" [id]="listboxId" role="listbox"
        [attr.aria-label]="column.label + ' options'"
        style="display: none;"
        (mousedown)="$event.stopPropagation()">
        <div *ngFor="let option of column.selectOptions; let i = index"
          class="listbox-option" role="option"
          [id]="optionId(i)"
          [attr.aria-selected]="isOptionSelected(option.value)"
          [class.selected]="isOptionSelected(option.value)"
          [class.highlighted]="highlightIndex === i"
          (mousedown)="$event.stopPropagation()"
          (click)="selectOption(option.value, $event)"
          (mouseenter)="highlightIndex = i">
          {{ option.label }}
        </div>
      </div>
    </div>
  `,
})
export class SelectEditorComponent extends BaseListboxEditor implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('trigger') trigger!: ElementRef<HTMLButtonElement>;
  private typeAheadBuffer = '';
  private typeAheadTimer: any = null;

  get listboxId(): string { return `listbox-${this.column.key}-${this.rowId}`; }
  get activeDescendantId(): string { return this.optionId(this.highlightIndex); }
  optionId(i: number): string { return `option-${this.column.key}-${this.rowId}-${i}`; }

  constructor(
    protected override readonly a11y: ChildTableA11yService,
    private cdr: ChangeDetectorRef,
    private positioning: ChildTablePositioningService,
  ) { super(); }

  ngOnInit(): void {
    this.editingValue = this.value;
    const idx = this.column.selectOptions?.findIndex(o => String(o.value) === String(this.editingValue)) ?? -1;
    this.highlightIndex = idx >= 0 ? idx : 0;
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      const triggerEl = this.trigger?.nativeElement;
      if (!triggerEl) return;
      triggerEl.focus();
      const listbox = triggerEl.closest('.cell-listbox-wrapper')?.querySelector('.cell-listbox') as HTMLElement;
      if (listbox) {
        listbox.style.minWidth = Math.max(triggerEl.getBoundingClientRect().width, 180) + 'px';
        this.positioning.openMenu('cell-select', triggerEl, listbox, () => this.cancel.emit());
      }
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void { this.positioning.closeMenu('cell-select'); }

  isOptionSelected(optionValue: any): boolean {
    return String(this.editingValue) === String(optionValue);
  }

  getOptionLabel(value: any): string {
    return this.column.selectOptions?.find(o => String(o.value) === String(value))?.label ?? String(value ?? '');
  }

  selectOption(value: any, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.editingValue = value;
    this.save.emit(this.editingValue);
  }

  onListboxKeydown(event: KeyboardEvent): void {
    const options = this.column.selectOptions;
    if (!options?.length) return;
    
    if (this.handleListboxNavigation(event, options)) {
      return;
    }

    switch (event.key) {
      case 'Enter': case ' ':
        event.preventDefault();
        event.stopPropagation();
        this.selectOption(options[this.highlightIndex].value);
        break;
      case 'Tab':
        event.preventDefault();
        event.stopPropagation();
        this.tabSave.emit({ value: options[this.highlightIndex].value, shiftKey: event.shiftKey });
        break;
      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        this.cancel.emit();
        break;
      default:
        if (event.key.length === 1 && !event.ctrlKey && !event.metaKey) {
          event.preventDefault();
          this.typeAheadBuffer += event.key.toLowerCase();
          clearTimeout(this.typeAheadTimer);
          this.typeAheadTimer = setTimeout(() => { this.typeAheadBuffer = ''; }, 500);
          const matchIdx = options.findIndex(o => o.label.toLowerCase().startsWith(this.typeAheadBuffer));
          if (matchIdx >= 0) {
            this.highlightIndex = matchIdx;
            this.a11y.announce(options[matchIdx].label);
          }
        }
    }
    this.cdr.markForCheck();
  }
}
